import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'

import { useQuery, useCommand } from '~/client/hooks/cqrpc'
import Link from '~/client/components/Link'
import ButtonRow from '~/client/components/ButtonRow'
import ErrorMessage from '~/client/components/ErrorMessage'
import Timestamp from '~/client/components/Timestamp'
import Form from '~/client/components/Form'
import FormButtonRow from '~/client/components/FormButtonRow'
import Heading from '~/client/components/Heading'
import SideNote from '~/client/components/SideNote'
import DocumentCreateForm from '~/client/components/DocumentCreateForm'
import JSONSchemaFormInput from '~/client/components/JSONSchemaFormInput'

export default function DocumentsPage() {
  return <Container>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Heading my={2} variant="main">Data Blocks</Heading>
      <ButtonRow>
        <Button
          variant="contained"
          component={Link}
          to="/documents/new"
        >New Document</Button>
        <Button
          variant="text"
          component={Link}
          to="/documents/types"
        >Document Types</Button>
      </ButtonRow>
    </Stack>
    <SideNote>
      A Data Block is a tightly defined and well described set of data attributes
      that are about a specific thing or subject area’ For example, Data Blocks
      might relate to things like 'My Home Address', or 'My Car', or 'My Preferences'.
    </SideNote>
    <MyDocumentsList />
  </Container>
}

function MyDocumentsList(){
  const query = useQuery('documents.getMine')
  const documents = query.result || []
  console.log('documents', documents)
  return <>
    <ErrorMessage error={query.error}/>
    <TableContainer>
      <Table sx={{
        minWidth: 650,
        maxWidth: '100%',
        whiteSpace: 'nowrap',
      }}>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Type</TableCell>
            <TableCell align="right">Created At</TableCell>
            <TableCell align="right">Updated At</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {documents.map(document => (
            <TableRow
              key={document.id}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell component="th" scope="row">
                <Link to={`/documents/${document.id}`}>
                  {document.name}
                </Link>
              </TableCell>
              <TableCell>
                <Link to={`/documents/types/${document.type.id}`}>
                  {document.type.name}
                </Link>
              </TableCell>
              <TableCell align="right">
                <Timestamp at={document.createdAt}/>
              </TableCell>
              <TableCell align="right">
                <Timestamp at={document.updatedAt}/>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </>
}

function New({ }){
  const navigate = useNavigate()
  return <Paper sx={{p: 2, my: 1}}>
    <DocumentCreateForm {...{
      onSuccess(newDocument){
        navigate(`/documents/${newDocument.id}`)
      },
    }}/>
  </Paper>
}

function Show({ }){
  const { id } = useParams()
  const query = useQuery('documents.get', { id })
  const document = query.result
  const currentValue = React.useMemo(
    () => document?.value,
    [document]
  )
  const update = useCommand('documents.update', {
    onSuccess(){ query.reload() }
  })
  const [newName, setNewName] = React.useState()
  const [changes, setChanges] = React.useState({})
  const onChange = combined => {
    console.log('ON CHANGE', combined)
    const changes = {}
    for (const prop in combined)
      if (currentValue[prop] !== combined[prop])
        changes[prop] = combined[prop]
    setChanges(changes)
  }
  const newValue = {...currentValue, ...changes}
  const submittable = !!(newName || Object.keys(changes).length > 0)
  return <Paper sx={{p: 2, my: 2, maxWidth: 'sm', mx: 'auto'}}>
    <Typography variant="h6">Document: {document ? (newName ?? document.name) : ``}</Typography>
    <ErrorMessage error={query.error}/>
    {query.pending ? <CircularProgress/> : null}
    {document
      ? <Form
        onSubmit={() => {
          update.call({ id, name: newName, value: newValue })
        }}
      >
        <Typography variant="body2">Version: {document.version}</Typography>
        <Typography variant="body2" mb={2}>
          Last Updated: <Timestamp variant="body2" at={document.updatedAt || document.createdAt}/>
        </Typography>
        <ErrorMessage error={update.error}/>
        <TextField {...{
          required: true,
          variant: 'standard',
          fullWidth: true,
          label: 'Document Name',
          name: 'filename',
          sx: {mb: 2},
          value: newName ?? document.name,
          onChange: e => {
            const newName = e.target.value
            setNewName(newName === document.name ? undefined : newName)
          }
        }}/>
        <JSONSchemaFormInput {...{
          schema: document.type.schema,
          value: newValue,
          onChange,
        }}/>
        <FormButtonRow {...{
          submittable,
          submitting: update.pending,
          submitText: 'Update',
          submittingText: 'Updating…',
        }}/>
      </Form>
      : null
    }
  </Paper>
}
